import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BuildingOffice2Icon, BuildingOfficeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Modal from '../feedBack/Modal';
import Loader from '../dataDisplay/Loader';
import { useDebounce } from '../../hooks/useDebounce';
import {
    FACILITY_SWITCHER_MIN_CHARS,
    FacilitySwitcherItem,
    useFacilitySwitcherSearch,
} from '../../api/superAdmin/facilitySwitcher/useFacilitySwitcherSearch';

const SEARCH_DEBOUNCE_MS = 250;

interface FacilitySearchModalProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    title: string;
    placeholder: string;
    size: 'md' | 'lg';
    /** Identifiant de la liste de résultats (attribut aria-controls du champ). */
    listboxId: string;
    /** Aide clavier affichée sous le champ (masquée au doigt). */
    keyboardHint: string;
    /** Contenu inséré entre le champ et les résultats. */
    beforeResults?: (close: () => void) => ReactNode;
    /** Entrée sur la ligne surlignée. `close` ferme la modale et vide la recherche. */
    onEnter: (facility: FacilitySwitcherItem, close: () => void) => void;
    /** Contenu d'une ligne de résultat (le cadre et le surlignage sont communs). */
    renderRow: (facility: FacilitySwitcherItem, close: () => void) => ReactNode;
}

/**
 * Base commune des recherches d'établissement super admin : champ loupe,
 * recherche différée, états (trop court, chargement, aucun résultat) et
 * navigation clavier (↑ ↓ pour surligner, Entrée pour `onEnter`).
 */
export default function FacilitySearchModal({
    open,
    setOpen,
    title,
    placeholder,
    size,
    listboxId,
    keyboardHint,
    beforeResults,
    onEnter,
    renderRow,
}: FacilitySearchModalProps) {
    const { t } = useTranslation(['navigation', 'common']);
    const [search, setSearch] = useState('');
    const [highlighted, setHighlighted] = useState(0);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

    const { data: results = [], isFetching } = useFacilitySwitcherSearch(debouncedSearch, open);
    const hasEnoughChars = debouncedSearch.trim().length >= FACILITY_SWITCHER_MIN_CHARS;

    useEffect(() => setHighlighted(0), [results]);

    const close = () => {
        setOpen(false);
        setSearch('');
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (results.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlighted((index) => Math.min(index + 1, results.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlighted((index) => Math.max(index - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            onEnter(results[highlighted], close);
        }
    };

    return (
        <Modal
            open={open}
            setOpen={(value) => (value ? setOpen(true) : close())}
            title={title}
            size={size}
            showFooter={false}
            initialFocus={inputRef}
        >
            <div className="relative mb-2">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <input
                    ref={inputRef}
                    type="search"
                    role="combobox"
                    aria-expanded={results.length > 0}
                    aria-controls={listboxId}
                    aria-label={title}
                    placeholder={placeholder}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-main-color"
                />
            </div>
            {/* Aide clavier : sans objet au doigt. */}
            <p className="mb-3 hidden text-xs text-gray-400 sm:block">{keyboardHint}</p>

            {beforeResults?.(close)}

            <div className="-mx-4 max-h-[360px] overflow-y-auto px-4">
                {!hasEnoughChars ? (
                    <p className="py-6 text-center text-sm text-gray-500">
                        {t('navigation:facility_switcher.min_chars', { count: FACILITY_SWITCHER_MIN_CHARS })}
                    </p>
                ) : isFetching && results.length === 0 ? (
                    <div className="flex justify-center py-6">
                        <Loader message={t('common:states.loading')} />
                    </div>
                ) : results.length === 0 ? (
                    <p className="py-6 text-center text-sm text-gray-500">{t('navigation:facility_switcher.no_results')}</p>
                ) : (
                    <ul id={listboxId} role="listbox" className="space-y-2">
                        {results.map((facility, index) => (
                            <li
                                key={facility.id}
                                role="option"
                                aria-selected={index === highlighted}
                                className={`rounded-lg border ${
                                    index === highlighted ? 'border-main-color bg-main-color-light/30' : 'border-gray-200'
                                }`}
                            >
                                {renderRow(facility, close)}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Modal>
    );
}

interface FacilityIdentityProps {
    facility: FacilitySwitcherItem;
    /** Établissement actuellement consulté : nom mis en avant. */
    isActive?: boolean;
}

/** Icône, nom, badges (groupe, démo) et groupes d'appartenance d'un résultat. */
export function FacilityIdentity({ facility, isActive = false }: FacilityIdentityProps) {
    const { t } = useTranslation('navigation');
    const Icon = facility.is_group ? BuildingOffice2Icon : BuildingOfficeIcon;
    const groupNames = facility.group_names?.join(', ');

    return (
        <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                facility.is_group ? 'bg-main-color-light' : 'bg-gray-100'
            }`}>
                <Icon className={`h-4 w-4 ${facility.is_group ? 'text-main-color' : 'text-gray-600'}`} aria-hidden="true" />
            </div>
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <span className={`truncate text-sm ${isActive ? 'font-semibold text-main-color' : 'font-medium text-gray-900'}`}>
                        {facility.name}
                    </span>
                    {facility.is_group ? (
                        <span className="rounded bg-main-color-light px-1.5 py-0.5 text-[10px] font-medium text-main-color-dark">
                            {t('facility_switcher.group_badge')}
                        </span>
                    ) : null}
                    {facility.is_demo ? (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                            {t('facility_switcher.demo_badge')}
                        </span>
                    ) : null}
                </div>
                {groupNames ? <p className="truncate text-xs text-gray-500">{groupNames}</p> : null}
            </div>
        </div>
    );
}
